import { call, put, SagaGenerator, select, takeLatest } from "typed-redux-saga";
import { Action, AnyAction } from "typescript-fsa";

import { Activity, ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { Events } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import {
	FormActivity,
	FormEngineActions,
	FormEngineSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { OverviewEngineSelectors } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/client-extensions";
import { WorkflowsActions } from "@com.mgmtp.a12.workflows/workflows-core/lib/actions";

import { updateIntegratedDocument } from "../../app/integratedDocuments/actions";
import {
	didUpdateOverviewDocument,
	DidUpdateOverviewDocumentPayload
} from "../../component/auxiliaryOverviewEngine/actions";
import { assertNonNullable, assertTrue } from "../../utilities/assert";
import { convertAppIdToKey } from "../../utilities/applicationKeyUtils";
import { selectHasNeededPermissions } from "../../utilities/user";
import { hasUpdateDocumentPermission } from "../../utilities/permissionUtils";
import { isString } from "../../utilities/typeGuardUtils";

import { ANTRAG_DETAIL_ACTIVITY_ID, TASK_DETAIL_ACTIVITY_ID } from "../processOverview/store/actions";
import { requestUpdateDueWiedervorlagenCount } from "../wiedervorlagen/actions";
import { WIEDERVORLAGEN_OVERVIEW_ACTIVITY } from "../wiedervorlagen/constants";
import { isDocumentWithIdAndProcessMetadata, isDocumentWithProcessMetadata } from "../workflows/data/processMetadata";

import { FRISTEN_ACTIVITY, SortingPaths } from "./constants";
import { dataModels } from "./moduleResources";

/**
 * Zeigt die Overview für Fristen an oder versteckt sie wieder
 */
export function* openFristenSaga(): SagaGenerator<void> {
	yield* takeLatest((action: AnyAction) => {
		return (
			FormEngineActions.event.match(action) &&
			Events.eventButtonTriggered.match(action.payload.engineEvent) &&
			action.payload.engineEvent.payload.name === "showFristen" &&
			!!action.payload.engineEvent.payload.buttonPath
		);
	}, openFristen);
}

function* openFristen(action: Action<FormEngineActions.FormEngineEventActions>): SagaGenerator<void> {
	const activity = yield* select(ActivitySelectors.activityById(action.payload.activityId));

	if (!activity) {
		throw new Error("Keine Activity gefunden.");
	}

	const sourceDescriptor = { ...(activity?.descriptor ?? {}) } as Record<string, string | undefined>;

	const processId = yield* call(getProcessIDFromActivity, activity.id);

	if (!processId) {
		console.error("No process ID in task activity");
		return;
	}

	const fvId = sourceDescriptor.fachverfahren;
	const activityDescriptor = {
		fachverfahren: fvId,
		instance: undefined,
		model: dataModels.FRIST_D,
		processId
	};

	const wasOpenBefore = yield* select(ActivitySelectors.activityById(FRISTEN_ACTIVITY));
	if (wasOpenBefore) {
		yield* put(ActivityActions.cancel({ activityId: FRISTEN_ACTIVITY }));
	} else {
		const wiedervorlagenActivity = yield* select(ActivitySelectors.activityById(WIEDERVORLAGEN_OVERVIEW_ACTIVITY));
		if (wiedervorlagenActivity) {
			yield* put(ActivityActions.cancel({ activityId: WIEDERVORLAGEN_OVERVIEW_ACTIVITY }));
		}
		const fvKey = fvId ? convertAppIdToKey(fvId) : undefined;
		const data = yield* select(ActivitySelectors.data(TASK_DETAIL_ACTIVITY_ID));
		const isFinished =
			FormActivity.Data.SingleDocumentData.isInstance(data) && isDocumentWithProcessMetadata(data.document)
				? data.document.root.processMetadata.isFinished
				: false;
		const taskUiState = yield* select(FormEngineSelectors.uiState(TASK_DETAIL_ACTIVITY_ID));
		const isTaskReadOnly = (taskUiState?.readonly ?? false) && !isFinished;
		const hasPermission = yield* select(selectHasNeededPermissions(hasUpdateDocumentPermission, fvKey));
		yield* put(
			ActivityActions.create({
				activityDescriptor,
				initiatingActivityId: action.payload.activityId,
				activityId: FRISTEN_ACTIVITY,
				slices: {
					uiState: {
						readonly: !hasPermission || isTaskReadOnly
					}
				}
			})
		);
	}
}

export function* handleDidUpdateFristInOverviewSaga(): SagaGenerator<void> {
	yield* takeLatest(
		(action: AnyAction) => {
			return (
				didUpdateOverviewDocument.match(action) && action.payload.updatedDocument.modelId === dataModels.FRIST_D
			);
		},
		function* ({ payload: { updatedDocument, activityId } }: Action<DidUpdateOverviewDocumentPayload>) {
			const processId: string = (updatedDocument as Activity.Data.Document)?.root?.ProcessId;
			assertNonNullable(processId);
			assertTrue(isString(updatedDocument.id));

			yield* put(
				updateIntegratedDocument({
					subdocument: updatedDocument,
					subdocumentId: updatedDocument.id.split("/")[1],
					processId
				})
			);

			yield* put(requestUpdateDueWiedervorlagenCount({ activityId: ANTRAG_DETAIL_ACTIVITY_ID }));

			const overviewState = yield* select(OverviewEngineSelectors.uiState(activityId));
			if (overviewState?.sorting?.some(s => s.path === SortingPaths.Erfuellt)) {
				yield* put(ActivityActions.reloadData({ activityId: activityId }));
			}
		}
	);
}

function* getProcessIDFromActivity(activityId: string): SagaGenerator<string | undefined> {
	const data = yield* select(ActivitySelectors.data(activityId));
	if (FormActivity.Data.SingleDocumentData.isInstance(data) && isDocumentWithIdAndProcessMetadata(data.document)) {
		return data.document.root.processMetadata.processId;
	}
	return undefined;
}

export function* closeFristenSaga(): SagaGenerator<void> {
	yield* takeLatest(WorkflowsActions.completeTask.done, function* () {
		const fristenActivity = yield* select(ActivitySelectors.activityById(FRISTEN_ACTIVITY));
		if (fristenActivity) {
			yield* put(ActivityActions.cancel({ activityId: FRISTEN_ACTIVITY }));
		}
	});
}

export const fristenSagas = [openFristenSaga, handleDidUpdateFristInOverviewSaga, closeFristenSaga];
